/******************************************************************************
 * PageView Hook
 * version: 1.0.2, release: 27.02.2021
 * author: albert lang, albert@jumptomorrow.com
 * ----------------------------------------------------------------------------
 * description:
 * 
 * dependencies: none
 * 
 * todos:
 * - history
 * - swipeview
 * 
******************************************************************************/


import React, { useEffect, useState, memo, useRef, useReducer } from 'react';
import './TreeView.scss';
import { Checkbox } from './ui-controls';


//----------------------------------------------------------------------------- parseCSS
const parseCSS = value => {
	return value && (typeof value === 'number'? value+'px' : value);
}


//============================================================================= TreeView Component
// every node item must have a unique id
// props:
//		selectNodes	: boolean - if nodes with subnodes are selectable
//
export default function TreeView(props){
	const [, render] = useReducer(e => !e);
	const selectedId = useRef(null);
	const selectedNode = useRef(null);

	// const source = useRef();

	// ------------------------------------------------------------------------ setup
	const [setup] = useState(() => {
		let fn = typeof props.setup === 'function'? props.setup : null;
		let s = (!fn && props.setup) || {};

		s.nodes = {};		//nodes lookup
		s.numNodes = 0;		//total number of all nodes
		s.render = render;

		s.selectNode = node => {
			onSelect(typeof node === 'string'? setup.nodes[node] : node);
		}

		s.root = {
			name		: 'root',
			id			: 'root',
			nodes		: [],
			numNodes	: 0,
			totalNodes	: 0,
			level		: 0,
			open		: true,
		}

		s.addNode = (node, parent = s.root, update = true) => {
			if (!node || !parent) return;

			if (Array.isArray(node)) {
				let nodes = node.map(n => s.addNode(n, parent, false));
				if (update) render();
				return nodes;
			}

			node = typeof node === 'string' ? { id: node, name: node } : { ...node };
			node.id = node.id ?? String('node-' + s.numNodes);
			node.name = node.name ?? node.id;

			if (s.nodes[node.id]) return s.nodes[node.id];

			node.parent = parent;
			if (!parent.nodes) {
				parent.nodes = [node];
				parent.numNodes = 1;
			} else if (!parent.nodes.find(n => n.id === node.id)) {
				parent.nodes.push(node);
				parent.numNodes++;
			}

			node.index = parent.numNodes - 1;
			node.level = parent.level + 1;

			let p = parent;
			while (p) {
				p.open = true;
				p.totalNodes = (p.totalNodes ?? 0) + 1;
				p = p.parent;
			}

			if (node.nodes) {
				node.totalNodes = 0;
				node.numNodes = 0;
				node.nodes = node.nodes.map(n => s.addNode(n, node));
			}

			s.nodes[node.id] = node;
			s.numNodes++;

			if (update) render();
			return node;
		}

		if (fn) fn(s);	//return setup if function
		return s
	})


	useEffect(() => {
		if (!props.source) return;

		props.source.map(e => setup.addNode(e, setup.root, false));
		selectedId.current = null;
		selectedNode.current = null;

		render();

	}, [props.source]);


	useEffect(() => {		//selection from outside
		let id = props.selectedId;
		if (id === selectedId.current) return;
		if (!id) onSelect(null);
		else onSelect(setup.nodes[id]);

	}, [props.selectedId]);



	// ------------------------------------------------------------------------ constructor
	useEffect(() => {
		// console.log('---init treeView')
		if (props.cache){
			let id = localStorage.getItem(props.cache);
			// if (id !== undefined) onSelect(getNode(id));
			if (id !== undefined) onSelect(setup.nodes[id]);
		}
		if (props.onInit) props.onInit();

	}, [])


	const onSelect = useRef(node => {					
		if (selectedNode.current === node) return;

		if (node) {
			if (props.onClick) props.onClick(node);
			if (node.enabled === false || (node.nodes && !props.selectNodes)) return;
		}

		clearCurrentSelection();

		if (!node) {
			clearSelection();
			return;
		}

		selectNode(node);
		openParentNodes(node);

		selectedId.current = node.id;
		if (props.cache) localStorage.setItem(props.cache, node.id);
		props.onSelect && props.onSelect(node);

		render();
	}).current;

	const clearCurrentSelection = () => {
		if (selectedNode.current) {
			selectedNode.current.selected = false;
			if (selectedNode.current._select) selectedNode.current._select(false);
		}
	};

	const clearSelection = () => {
		selectedId.current = null;
		selectedNode.current = null;
		if (props.cache) localStorage.removeItem(props.cache);
	};

	const selectNode = node => {
		selectedNode.current = node;
		node.selected = true;
		if (node._select) node._select(true);
	};

	const openParentNodes = node => {
		let p = node.parent;
		while (p) {
			p.open = true;
			p = p.parent;
		}
	};


	return (
		<div className='ui2-tree-view'
			id={props.id}
			style={{width:parseCSS(props.width), 'overflowY':props.scroll?'auto':'hidden', ...props.style}}
			>
			<div className='ui2-tree-view-container'>
				{setup.root.nodes.map((e, i) => 
					<TreeItem
						key={i}
						index={i}
						className='ui2-tree-view-item'
						data={e}
						onClick={onSelect}
						level={0}
						multiselect={props.multiselect}

						numNodes={e.numNodes}
						totalNodes={e.totalNodes}
						selected={e.selected}
						open={e.open}
					/>
				)}
			</div>
		</div>
	)
};


//============================================================================= TreeItem Component
/*	props.data attributes:
	id:	generic tree-node id
*/
const TreeItem = memo(props => {
	const [open, setOpen] = useState();
	const [selected, setSelected] = useState();
	const hasNodes = !!props.data.nodes;
	const textIndent = (hasNodes? props.level + 1 : props.level) + 'em';

	useEffect(() => {
		if (props.open !== open) setOpen(props.open);
		if (props.selected !== selected) setSelected(props.selected);

	}, [props.selected, props.open])


	useEffect(() => {		//mount
		props.data._select = setSelected;

		return(() => {		//unmount
		})
	}, [])


	return (
		<div
			className={'ui2-tree-view-item'
				+ (selected? ' selected' : '')
				+ (open? ' open' : '')
				+ (props.data?.enabled === false? ' disabled' : '')
			}
			id={props.data.id}
			style={{textIndent}}
			onClick={e => {
				e.preventDefault();
				e.stopPropagation();
				if (hasNodes){
					// if (!props.data.nodes.length) return;
					props.data.open = !open;
					setOpen(!open);
				}
				props.onClick(props.data);
			}}
			>
			{hasNodes &&
				<div className={'ui2-tree-view-symbol' + (open? ' open' : '')}
					style={{left:textIndent}}
				/>
			}

			<div className={'ui2-tree-view-label' + (selected? ' selected' : '')}>
				<div className='ui2-tree-view-label-text'>
					{props.data.label || props.data.name || String(props.data.id)}
				</div>
				{props.multiselect && <Checkbox />}
			</div>

			{(hasNodes && open) &&
				<div className='ui2-tree-view-node'>
					{props.data.nodes.map((e, i) => <TreeItem
						key={i}
						index={i}
						className='ui2-tree-view-item'
						data={e}
						onClick={props.onClick}
						level={props.level + 1}
						multiselect={props.multiselect}

						numNodes={e.numNodes}
						totalNodes={e.totalNodes}
						selected={e.selected}
						open={e.open}
					/>)}
				</div>
			}
		</div>
	)
});

